// Example usage of auto-generated FeatureKey enum
import { useMemo, ReactNode } from "react";
import { useSelector } from "react-redux";
import { FeatureKey, isValidFeatureKey } from "../types/generated/FeatureKey";
import { RootState } from "../types/RootState";
import { store } from "../redux/store";

// Your preferred computed map approach
export const useFeatures = () => {
  const userFeatures = useSelector(
    (state: RootState) => state.user.features || {}
  );

  return useMemo(() => {
    const featureMap: Record<string, boolean> = {};

    // Auto-generate map for all features from Java enum
    Object.values(FeatureKey).forEach((key) => {
      featureMap[key] = Boolean(userFeatures[key]);
    });

    return featureMap;
  }, [userFeatures]);
};

// Helper function that uses the hook properly
export const useHasFeature = (feature: FeatureKey): boolean => {
  const features = useFeatures();
  return features[feature] || false;
};

// Non-hook version that can be called anywhere (including inside callbacks)
// Gets features from Redux store directly, or from passed loginInfo
export const hasFeature = (
  feature: FeatureKey,
  loginInfo?: { features?: { [key: string]: boolean } } | null
): boolean => {
  try {
    // If loginInfo is provided, use it directly
    if (loginInfo !== undefined) {
      const userFeatures = loginInfo?.features || {};
      return Boolean(userFeatures[feature]);
    }
    // Otherwise, get from Redux store
    const state = store.getState();
    const userFeatures = state.user?.features || {};
    return Boolean(userFeatures[feature]);
  } catch {
    return false;
  }
};

// API response validation
export const parseFeatures = (
  apiFeatures: unknown
): Record<string, boolean> => {
  if (!apiFeatures || typeof apiFeatures !== "object") return {};

  const features: Record<string, boolean> = {};
  const featuresObj = apiFeatures as Record<string, unknown>;

  // Only include valid feature keys and convert values to boolean
  Object.keys(featuresObj).forEach((key) => {
    if (isValidFeatureKey(key)) {
      features[key] = Boolean(featuresObj[key]);
    }
  });

  return features;
};

// Example component usage
export const FeatureGuard = ({
  feature,
  children,
}: {
  feature: FeatureKey;
  children: ReactNode;
}) => {
  const hasFeature = useHasFeature(feature);

  // Your pattern: features["feature_key"] => hasFeature(FeatureKey.FEATURE_KEY)
  return hasFeature ? <>{children}</> : null;
};
